import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, ExternalLink, Pencil, Plus, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";

import { curriculumService, type Lesson, type Progress, type Subject, type Syllabus, type Topic } from "../../services/curriculumService";
import { apiErrorMessage } from "../../utils/apiError";
import { ConfirmDialog, type ConfirmRequest } from "../ConfirmDialog";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, UsageNotice, deleteErrorText, type NoticeState } from "../register/RecordParts";
import { describeUsage, type RegisterConfig } from "../register/registerLogic";
import { LessonForm, SyllabusForm, TopicForm } from "./SyllabusForms";
import { DIFFICULTIES, LESSON_TYPES, activeStatus, coverageByClass, inTeachingOrder, labelFrom, lessonsInOrder, progressStatus, type SyllabusRow } from "./syllabusLogic";

type Editing =
  | { kind: "syllabus" }
  | { kind: "topic"; record?: Topic }
  | { kind: "lesson"; topic: Topic; record?: Lesson };

const coverageConfig: RegisterConfig<Progress> = {
  search: (row) => [row.class_name, row.topic_name, row.teacher_name, row.notes].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "in_progress", label: "Being taught", match: (row) => row.status === "in_progress" },
    { key: "completed", label: "Taught", match: (row) => row.status === "completed" },
    { key: "delayed", label: "Behind", match: (row) => row.status === "delayed" },
  ],
  date: { label: "Started", value: (row) => row.start_date },
  category: { label: "Class", value: (row) => row.class_name },
  sorts: [
    { key: "class", label: "Class, then topic", compare: (a, b) => a.class_name.localeCompare(b.class_name) || a.topic_name.localeCompare(b.topic_name) },
    { key: "-start", label: "Newest first", compare: (a, b) => b.start_date.localeCompare(a.start_date) },
  ],
};
const coverageColumns: RegisterColumn<Progress>[] = [
  { header: "Class", cell: (row) => <span className="font-medium text-gray-900">{row.class_name}</span>, exportValue: (row) => row.class_name },
  { header: "Topic", cell: (row) => row.topic_name, exportValue: (row) => row.topic_name, className: "min-w-[10rem] whitespace-normal text-gray-700" },
  { header: "Teacher", cell: (row) => row.teacher_name, exportValue: (row) => row.teacher_name, className: "text-gray-700" },
  { header: "Started", cell: (row) => row.start_date, exportValue: (row) => row.start_date, className: "tabular-nums text-gray-700" },
  { header: "Finished", cell: (row) => row.completion_date || <span className="text-gray-300">—</span>, exportValue: (row) => row.completion_date ?? "", className: "tabular-nums text-gray-700" },
  { header: "Done", cell: (row) => `${Math.round(Number(row.progress_percentage))}%`, exportValue: (row) => Number(row.progress_percentage), align: "right" },
  { header: "Status", cell: (row) => <StatusDot {...progressStatus(row)} />, exportValue: (row) => progressStatus(row).label },
];

function Bar({ value }: { value: number }) {
  return (
    <span className="flex items-center gap-2" aria-label={`${value}% covered`}>
      <span className="h-1.5 w-28 overflow-hidden rounded-full bg-gray-100">
        <span className="block h-full rounded-full bg-primary-600" style={{ width: `${value}%` }} />
      </span>
      <span className="text-xs tabular-nums text-gray-600">{value}%</span>
    </span>
  );
}

/**
 * One syllabus as a teaching plan: its aims, the sequence of topics and their
 * lesson plans, and how far each class has got. Leadership changes the
 * syllabus itself; its subject teachers plan its topics and lessons.
 */
export function SyllabusWorkspace({ syllabus, topics, lessons, progress, subjects, levels, canLead, onBack, onChanged, onRemoved }: {
  syllabus: SyllabusRow;
  topics: Topic[];
  lessons: Lesson[];
  progress: Progress[];
  subjects: Subject[];
  levels: string[];
  /** access.curriculum.leadership: may change the syllabus record itself. */
  canLead: boolean;
  onBack: () => void;
  onChanged: () => void;
  onRemoved: (message: string) => void;
}) {
  const canPlan = Boolean(syllabus.can_edit);
  const [notice, setNotice] = useState<NoticeState>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [openTopic, setOpenTopic] = useState<number | null>(null);
  const [openLesson, setOpenLesson] = useState<number | null>(null);
  const [dialog, setDialog] = useState<ConfirmRequest | null>(null);
  const usage = useQuery({ queryKey: ["syllabus-usage", syllabus.id], queryFn: () => curriculumService.getSyllabus(syllabus.id), enabled: canLead });

  const own = inTeachingOrder(topics.filter((topic) => topic.syllabus === syllabus.id));
  const lessonsOf = (topic: Topic) => lessonsInOrder(lessons.filter((lesson) => lesson.topic === topic.id));
  const records = progress.filter((record) => record.syllabus === syllabus.id);
  const classes = coverageByClass(own, records);
  const done = (text: string) => {
    setEditing(null);
    setNotice({ tone: "success", text });
    onChanged();
  };

  const saveSyllabus = useMutation({
    mutationFn: (data: Partial<Syllabus>) => curriculumService.updateSyllabus(syllabus.id, data),
    onSuccess: () => done("Syllabus saved."),
  });
  const toggle = useMutation({
    mutationFn: () => curriculumService.updateSyllabus(syllabus.id, { is_active: !syllabus.is_active }),
    onSuccess: () => done(syllabus.is_active ? "Syllabus made inactive. It stays on record and can be made active again." : "Syllabus made active."),
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "It could not be changed.") }),
  });
  const saveTopic = useMutation({
    mutationFn: ({ id, ...data }: Partial<Topic> & { id?: number }) => (id ? curriculumService.updateTopic(id, data) : curriculumService.createTopic(data)),
    onSuccess: (_result, variables) => done(variables.id ? "Topic saved." : "Topic added."),
  });
  const saveLesson = useMutation({
    mutationFn: ({ id, ...data }: Partial<Lesson> & { id?: number }) => (id ? curriculumService.updateLesson(id, data) : curriculumService.createLesson(data)),
    onSuccess: (_result, variables) => done(variables.id ? "Lesson plan saved." : "Lesson plan added."),
  });
  const remove = useMutation({
    mutationFn: async ({ kind, id }: { kind: "syllabus" | "topic" | "lesson"; id: number }) => {
      if (kind === "syllabus") await curriculumService.deleteSyllabus(id);
      if (kind === "topic") await curriculumService.deleteTopic(id);
      if (kind === "lesson") await curriculumService.deleteLesson(id);
      return kind;
    },
    onSuccess: (kind) => {
      setDialog(null);
      setOpenTopic(null);
      setOpenLesson(null);
      if (kind === "syllabus") onRemoved(`${syllabus.title} deleted.`);
      else done(kind === "topic" ? "Topic removed." : "Lesson plan removed.");
    },
    onError: (error) => {
      setDialog(null);
      setNotice({ tone: "error", text: deleteErrorText(error) });
    },
  });

  const topic = openTopic !== null && !editing && !dialog ? own.find((item) => item.id === openTopic) : undefined;
  const lesson = openLesson !== null && !editing && !dialog ? lessons.find((item) => item.id === openLesson) : undefined;
  const lessonTopic = lesson ? own.find((item) => item.id === lesson.topic) : undefined;
  const topicCoverage = (item: Topic) => records.filter((record) => record.topic === item.id && record.status === "completed").length;

  return (
    <div className="space-y-6">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline">
        <ArrowLeft className="h-4 w-4" /> All syllabi
      </button>

      <section className="card">
        <div className="card-content space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-primary-700">{syllabus.curriculum_name}</p>
              <h1 className="mt-1 text-2xl font-bold text-gray-900">{syllabus.title}</h1>
              <p className="mt-1 text-sm text-gray-600">
                {syllabus.subject_name} · {syllabus.grade_name} (every stream) · version {syllabus.version} · in use from {syllabus.start_date}
                {syllabus.end_date ? ` to ${syllabus.end_date}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusDot {...activeStatus(syllabus)} />
              {canLead && (
                <>
                  <button type="button" disabled={toggle.isPending} onClick={() => toggle.mutate()} className="btn btn-outline gap-1.5">
                    {syllabus.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                    {syllabus.is_active ? "Make inactive" : "Make active"}
                  </button>
                  <button type="button" onClick={() => { saveSyllabus.reset(); setEditing({ kind: "syllabus" }); }} className="btn btn-primary gap-1.5">
                    <Pencil className="h-4 w-4" /> Edit
                  </button>
                </>
              )}
            </div>
          </div>
          {!canPlan && (
            <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
              You can read this plan. Its topics and lessons are planned by the teachers of {syllabus.subject_name} in {syllabus.grade_name} and by academic leadership.
            </p>
          )}
          {canLead && usage.data && (
            describeUsage(usage.data.in_use) ? (
              <UsageNotice inUse={usage.data.in_use} advice="Make it inactive if it is no longer followed." />
            ) : (
              <p className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                Nothing has been planned under it yet, so it can be deleted.
                <button
                  type="button"
                  onClick={() => setDialog({ title: "Delete syllabus?", body: <p><strong>{syllabus.title}</strong> will be removed. It has no topics, and this cannot be undone.</p>, confirmLabel: "Delete syllabus", tone: "danger", onConfirm: () => remove.mutate({ kind: "syllabus", id: syllabus.id }) })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-red-700 hover:underline"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </p>
            )
          )}
          <dl className="grid gap-3 sm:grid-cols-4">
            {[
              ["Topics", String(own.length)],
              ["Weeks planned", String(syllabus.weeks)],
              ["Lesson plans", String(syllabus.lessonCount)],
              ["Covered on average", syllabus.coverage === null ? "Not recorded" : `${syllabus.coverage}%`],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-gray-200 px-4 py-3">
                <dt className="text-xs font-medium text-gray-500">{label}</dt>
                <dd className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      <section className="card">
        <div className="card-header pb-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold text-gray-900">Teaching sequence</h2>
              <p className="mt-1 text-sm text-gray-500">Topics in the order they are taught. Open one for its objectives and lesson plans.</p>
            </div>
            {canPlan && (
              <button type="button" onClick={() => { saveTopic.reset(); setEditing({ kind: "topic" }); }} className="btn-primary h-8 min-h-8 px-3 text-xs">
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Add topic
              </button>
            )}
          </div>
        </div>
        <div className="card-content">
          {own.length ? (
            <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {own.map((item, index) => {
                const plans = lessonsOf(item);
                const taughtIn = topicCoverage(item);
                return (
                  <li key={item.id}>
                    <button type="button" onClick={() => setOpenTopic(item.id)} className="flex w-full items-start gap-4 px-4 py-3 text-left hover:bg-gray-50">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-700">{index + 1}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-gray-900">{item.name}</span>
                        <span className="mt-0.5 block text-xs text-gray-500">
                          {item.duration_weeks} {item.duration_weeks === 1 ? "week" : "weeks"} · {labelFrom(DIFFICULTIES, item.difficulty_level)} · {plans.length} {plans.length === 1 ? "lesson plan" : "lesson plans"}
                          {classes.length > 0 && ` · taught in ${taughtIn} of ${classes.length} ${classes.length === 1 ? "class" : "classes"}`}
                        </span>
                        {item.description && <span className="mt-1 block text-sm text-gray-600">{item.description}</span>}
                      </span>
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
              {canPlan ? "No topics yet. Add the first topic to begin the teaching plan." : "No topics have been planned yet."}
            </p>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-header pb-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold text-gray-900">Coverage by class</h2>
              <p className="mt-1 text-sm text-gray-500">How far each class has got, from what teachers record as they teach.</p>
            </div>
            <Link to="/curriculum" className="btn btn-outline h-8 min-h-8 gap-1.5 px-3 text-xs">
              <ExternalLink className="h-3.5 w-3.5" /> Record coverage
            </Link>
          </div>
        </div>
        <div className="card-content space-y-4">
          {classes.length ? (
            <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {classes.map((row) => (
                <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 text-sm">
                  <span className="min-w-[6rem] font-medium text-gray-900">{row.name}</span>
                  <span className="text-xs text-gray-500">{row.taught} of {own.length} topics taught · {row.teachers.join(", ") || "—"}</span>
                  <Bar value={row.coverage} />
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-gray-500">No class has recorded teaching against this syllabus yet.</p>
          )}
        </div>
      </section>

      {records.length > 0 && (
        <ClientRegister
          title="Coverage records"
          description="Every topic each class has started, with who taught it."
          rows={records}
          config={coverageConfig}
          columns={coverageColumns}
          rowKey={(row) => row.id}
          noun={["record", "records"]}
          exportName={{ file: `coverage-${syllabus.subject_code ?? syllabus.id}-${syllabus.grade_name}`, title: `Coverage — ${syllabus.title}` }}
          emptyText="No coverage recorded."
        />
      )}

      <section className="card">
        <div className="card-header pb-2">
          <h2 className="font-semibold text-gray-900">About this syllabus</h2>
        </div>
        <div className="card-content grid gap-5 md:grid-cols-2">
          {([
            ["Overview", syllabus.description],
            ["Learning outcomes", syllabus.learning_outcomes],
            ["What is assessed", syllabus.assessment_objectives],
            ["How it is assessed", syllabus.assessment_methods],
            ["Grading", syllabus.grading_criteria],
            ["Recommended books", syllabus.recommended_books],
            ["Teaching materials", syllabus.teaching_materials],
            ["Digital resources", syllabus.digital_resources],
            ["Notes", syllabus.notes],
          ] as [string, string | undefined][])
            .filter(([, body]) => body?.trim())
            .map(([title, body]) => (
              <section key={title}>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
                <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">{body}</p>
              </section>
            ))}
          {![syllabus.description, syllabus.learning_outcomes, syllabus.assessment_methods, syllabus.recommended_books, syllabus.teaching_materials].some((body) => body?.trim()) && (
            <p className="text-sm text-gray-500">{canLead ? "No aims or resources written yet. Use Edit to add them." : "No aims or resources have been written yet."}</p>
          )}
        </div>
      </section>

      {topic && (
        <DetailsModal
          size="3xl"
          title={`${own.indexOf(topic) + 1}. ${topic.name}`}
          headline={{ label: "Topic", value: `${topic.duration_weeks} ${topic.duration_weeks === 1 ? "week" : "weeks"}`, note: `${labelFrom(DIFFICULTIES, topic.difficulty_level)} · ${lessonsOf(topic).length} lesson plans` }}
          facts={[
            ["Place in the sequence", `${own.indexOf(topic) + 1} of ${own.length}`],
            ["Weeks", String(topic.duration_weeks)],
            ["Level of challenge", labelFrom(DIFFICULTIES, topic.difficulty_level)],
            ["Taught in", classes.length ? `${topicCoverage(topic)} of ${classes.length} ${classes.length === 1 ? "class" : "classes"}` : "No record yet"],
          ]}
          sections={[
            { title: "Description", body: topic.description },
            { title: "Learning objectives", body: topic.learning_objectives },
            { title: "Key concepts", body: topic.key_concepts },
            { title: "Skills developed", body: topic.skills_to_develop },
            { title: "Teaching approaches", body: topic.teaching_strategies },
            { title: "Learning activities", body: topic.learning_activities },
            { title: "Resources needed", body: topic.resources_needed },
            { title: "How it is assessed", body: topic.assessment_methods },
          ]}
          onClose={() => setOpenTopic(null)}
          footer={
            <>
              {canPlan && (
                <button
                  type="button"
                  onClick={() => setDialog({
                    title: "Remove topic?",
                    body: <p><strong>{topic.name}</strong>{lessonsOf(topic).length ? ` and its ${lessonsOf(topic).length} lesson plans` : ""} will be removed. This cannot be undone. A topic teachers have recorded coverage against is kept.</p>,
                    confirmLabel: "Remove topic",
                    tone: "danger",
                    onConfirm: () => remove.mutate({ kind: "topic", id: topic.id }),
                  })}
                  className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" /> Remove
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setOpenTopic(null)}>Close</button>
              {canPlan && (
                <>
                  <button type="button" className="btn btn-outline gap-1.5" onClick={() => { saveTopic.reset(); setEditing({ kind: "topic", record: topic }); }}>
                    <Pencil className="h-4 w-4" /> Edit
                  </button>
                  <button type="button" className="btn btn-primary gap-1.5" onClick={() => { saveLesson.reset(); setEditing({ kind: "lesson", topic }); }}>
                    <Plus className="h-4 w-4" /> Lesson plan
                  </button>
                </>
              )}
            </>
          }
        >
          <section>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Lesson plans</h4>
            {lessonsOf(topic).length ? (
              <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {lessonsOf(topic).map((item) => (
                  <li key={item.id}>
                    <button type="button" onClick={() => { setOpenTopic(null); setOpenLesson(item.id); }} className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-left text-sm hover:bg-gray-50">
                      <span className="font-medium text-gray-900">{lessonsOf(topic).indexOf(item) + 1}. {item.title}</span>
                      <span className="text-xs text-gray-500">{labelFrom(LESSON_TYPES, item.lesson_type)} · {item.duration_minutes} minutes</span>
                    </button>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-gray-500">No lesson plans yet.</p>
            )}
          </section>
        </DetailsModal>
      )}

      {lesson && lessonTopic && (
        <DetailsModal
          size="3xl"
          title={lesson.title}
          headline={{ label: `Lesson ${lessonsOf(lessonTopic).findIndex((item) => item.id === lesson.id) + 1} of ${lessonTopic.name}`, value: `${lesson.duration_minutes} minutes`, note: labelFrom(LESSON_TYPES, lesson.lesson_type) }}
          facts={[
            ["Topic", lessonTopic.name],
            ["Kind of lesson", labelFrom(LESSON_TYPES, lesson.lesson_type)],
            ["Minutes", String(lesson.duration_minutes)],
          ]}
          sections={[
            { title: "Learning objectives", body: lesson.learning_objectives },
            { title: "How you will know they have learnt it", body: lesson.success_criteria },
            { title: "Introduction", body: lesson.introduction },
            { title: "Main activities", body: lesson.main_activities },
            { title: "Conclusion", body: lesson.conclusion },
            { title: "Homework", body: lesson.homework },
            { title: "Teaching materials", body: lesson.teaching_materials },
            { title: "How it is assessed", body: lesson.assessment_methods },
          ]}
          onClose={() => setOpenLesson(null)}
          footer={
            <>
              {canPlan && (
                <button
                  type="button"
                  onClick={() => setDialog({ title: "Remove lesson plan?", body: <p><strong>{lesson.title}</strong> will be removed. This cannot be undone.</p>, confirmLabel: "Remove lesson plan", tone: "danger", onConfirm: () => remove.mutate({ kind: "lesson", id: lesson.id }) })}
                  className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" /> Remove
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => { setOpenLesson(null); setOpenTopic(lessonTopic.id); }}>Back to the topic</button>
              {canPlan && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => { saveLesson.reset(); setEditing({ kind: "lesson", topic: lessonTopic, record: lesson }); }}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        />
      )}

      {editing?.kind === "syllabus" && (
        <SyllabusForm
          record={syllabus}
          subjects={subjects}
          levels={levels}
          busy={saveSyllabus.isPending}
          error={saveSyllabus.isError ? apiErrorMessage(saveSyllabus.error, "The syllabus could not be saved.") : undefined}
          onSave={(data) => saveSyllabus.mutate(data)}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.kind === "topic" && (
        <TopicForm
          syllabus={syllabus}
          record={editing.record}
          nextOrder={own.length ? Math.max(...own.map((item) => item.order ?? 0)) + 1 : 1}
          busy={saveTopic.isPending}
          error={saveTopic.isError ? apiErrorMessage(saveTopic.error, "The topic could not be saved.") : undefined}
          onSave={(data) => saveTopic.mutate({ ...data, id: editing.record?.id })}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.kind === "lesson" && (
        <LessonForm
          syllabus={syllabus}
          topic={editing.topic}
          record={editing.record}
          nextOrder={lessonsOf(editing.topic).length ? Math.max(...lessonsOf(editing.topic).map((item) => item.order ?? 0)) + 1 : 1}
          busy={saveLesson.isPending}
          error={saveLesson.isError ? apiErrorMessage(saveLesson.error, "The lesson plan could not be saved.") : undefined}
          onSave={(data) => saveLesson.mutate({ ...data, id: editing.record?.id })}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmDialog request={dialog} onClose={() => setDialog(null)} busy={remove.isPending} />
    </div>
  );
}
